import { useCallback, useEffect, useMemo, useState } from 'react';
import merge from 'lodash/merge';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { complianceAlertLevelsSelectors } from '@/redux/selectors';
import { complianceAlertLevelsActions } from '@/redux/actions';
import { useIsScrolled } from '@/hooks/useIsScrolled';
import { Loader } from '@/components';
import { Direction, UpdateTransactionAlertRequest } from '@/api';
import { RiskKeys } from '@/config/riskSignals';

import TransactionAlertsSetDefault from './components/TransactionAlertsSetDefault/TransactionAlertsSetDefault';
import TransactionAlertsDataTabs from './components/TransactionAlertsDataTabs/TransactionAlertsDataTabs';
import { TransactionAlertsTable } from './components/TransactionAlertsTable/TransactionAlertsTable';
import { TransactionAlertsEditModal } from './components/TransactionAlertsEditModal/TransactionAlertsEditModal';
import { TransactionAlertsEditValue } from './components/TransactionAlertsEditModal/TransactionAlertsEditModal.constants';
import { transactionAlertsDemoData } from './TransactionAlerts.constants';
import TransactionAlertsResetCategoryModal from './components/TransactionAlertsResetCategoryModal/TransactionAlertsResetCategoryModal';
import {
  getTransactionAlertsEmptyData,
  getFilteredByDirectionLevelsData,
} from './TransactionAlerts.helpers';
import styles from './TransactionAlerts.module.sass';

interface TransactionAlertsProps {
  isShowDemoData?: boolean;
}

const TransactionAlerts = ({ isShowDemoData }: TransactionAlertsProps) => {
  const dispatch = useAppDispatch();

  const { data: transactionAlertsData, loading } = useAppSelector(
    complianceAlertLevelsSelectors.getComplianceAlertLevelsSlice,
  );

  const [editModalData, setEditModalData] =
    useState<TransactionAlertsEditValue>();
  const [categoryToReset, setCategoryToReset] = useState<RiskKeys | null>(null);

  const { scrollRef, isScrolled, onScroll } = useIsScrolled({
    delta: 10,
  });

  const [currentDirection, setCurrentDirection] = useState<Direction>('income');

  const data = useMemo(() => {
    if (isShowDemoData) {
      return transactionAlertsDemoData;
    }

    const categories = transactionAlertsData?.categories ?? [];
    const emptyLevels = getTransactionAlertsEmptyData(categories);
    return {
      ...transactionAlertsData,
      levels: merge({}, emptyLevels, transactionAlertsData?.levels || {}),
    };
  }, [isShowDemoData, transactionAlertsData]);

  const filteredByDirectionLevelsData = useMemo(
    () => getFilteredByDirectionLevelsData(data?.levels, currentDirection),
    [data, currentDirection],
  );

  const handleFetchData = useCallback(() => {
    dispatch(complianceAlertLevelsActions.listAlertLevels({}));
  }, []);

  const handleOnChange = useCallback((val: UpdateTransactionAlertRequest) => {
    dispatch(complianceAlertLevelsActions.updateSettings(val));
    setEditModalData(undefined);
  }, []);

  useEffect(() => {
    if (!isShowDemoData) {
      handleFetchData();
    }
  }, [isShowDemoData]);

  return (
    <>
      {!isShowDemoData && (
        <div className={styles.actions}>
          <TransactionAlertsSetDefault onReset={handleFetchData} />
        </div>
      )}

      <div className={styles.alerts}>
        <TransactionAlertsDataTabs
          activeTab={currentDirection}
          onTabChange={setCurrentDirection}
        />

        <div className={styles.content}>
          <div className={styles.scroll} ref={scrollRef} onScroll={onScroll}>
            <TransactionAlertsTable
              data={filteredByDirectionLevelsData}
              isScrolled={isScrolled}
              isEditable={!isShowDemoData}
              onEdit={(val) => setEditModalData(val)}
              onClearCategory={setCategoryToReset}
            />
          </div>

          {loading && (
            <div className={styles.loader}>
              <Loader size="medium" />
            </div>
          )}
        </div>
      </div>

      <TransactionAlertsResetCategoryModal
        isOpen={!!categoryToReset}
        category={categoryToReset}
        direction={currentDirection}
        onReset={handleFetchData}
        onClose={() => setCategoryToReset(null)}
      />

      <TransactionAlertsEditModal
        isOpen={!!editModalData}
        isLoading={loading}
        value={editModalData}
        data={data?.levels ?? {}}
        onClose={() => setEditModalData(undefined)}
        onChange={handleOnChange}
      />
    </>
  );
};

export default TransactionAlerts;
